// == responsive ==

"use client"
import { useState, useEffect, useRef } from "react"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Menu, Bell, RotateCcw } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { NotificationModal } from "./notification-modal"

interface HeaderProps {
  onSidebarToggle: () => void
}

export function Header({ onSidebarToggle }: HeaderProps) {
  const [notificationCount, setNotificationCount] = useState(0)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [userName, setUserName] = useState("John Doe") // Default fallback
  const [userPhoto, setUserPhoto] = useState<string | null>(null)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [isHardRefreshDisabled, setIsHardRefreshDisabled] = useState(false)
  const [hardRefreshRemainingSeconds, setHardRefreshRemainingSeconds] = useState<number | null>(null)
  const hardRefreshTimerRef = useRef<NodeJS.Timeout | null>(null)

  const HARD_REFRESH_COOLDOWN_MS = 5 * 60 * 1000 // 5 minutes
  const HARD_REFRESH_STORAGE_KEY = "hardRefreshLastAt"

  // Get system name based on environment
  const getSystemName = (): string => {
    const env = (process.env.REACT_APP_SYS_ENV || "").toLowerCase()
    const baseName = "Timesheet Management System"
    
    if (env === "dev") {
      return `${baseName} [Dev]`
    } else if (env === "qa") {
      return `${baseName} [QA]`
    } else {
      // For prod or any other environment, return base name only
      return baseName
    }
  }

  // Generate initials from username
  const getInitials = (name: string): string => {
    if (!name) return "JD"
    const words = name.trim().split(" ")
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }

  // Fetch notification count
  const fetchNotificationCount = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/api/notification/count", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch notification count: ${response.status}`)
      }
      
      const data: { NotificationCount?: number; notificationCount?: number } = await response.json()
      // Handle both PascalCase and camelCase response formats
      setNotificationCount(data.NotificationCount ?? data.notificationCount ?? 0)
    } catch (err) {
      console.error("Error fetching notification count:", err)
      // Don't set error state, just log it
    }
  }

  // Load user name/photo from sessionStorage/localStorage on component mount
  useEffect(() => {
    const storedUserName = sessionStorage.getItem("userName") || localStorage.getItem("userName")
    ////console.log("[Header] storedUserName from localStorage:", storedUserName)
    if (storedUserName) {
      setUserName(storedUserName)
    }
    const storedPhoto = sessionStorage.getItem("userPhoto")
    if (storedPhoto) {
      setUserPhoto(storedPhoto)
    }

    // Fetch notification count on mount
    fetchNotificationCount()
  }, [])

  // Initialize hard-refresh cooldown state from localStorage
  useEffect(() => {
    const lastAt = localStorage.getItem(HARD_REFRESH_STORAGE_KEY)
    if (!lastAt) return

    const lastTs = Number(lastAt)
    if (Number.isNaN(lastTs)) {
      localStorage.removeItem(HARD_REFRESH_STORAGE_KEY)
      return
    }

    const now = Date.now()
    const diff = now - lastTs
    if (diff < HARD_REFRESH_COOLDOWN_MS) {
      const remainingMs = HARD_REFRESH_COOLDOWN_MS - diff
      setIsHardRefreshDisabled(true)
      setHardRefreshRemainingSeconds(Math.ceil(remainingMs / 1000))

      // Start a countdown timer
      hardRefreshTimerRef.current = setInterval(() => {
        setHardRefreshRemainingSeconds((prev) => {
          if (prev === null) return null
          if (prev <= 1) {
            // Cooldown finished
            if (hardRefreshTimerRef.current) {
              clearInterval(hardRefreshTimerRef.current)
            }
            localStorage.removeItem(HARD_REFRESH_STORAGE_KEY)
            setIsHardRefreshDisabled(false)
            return null
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (hardRefreshTimerRef.current) {
        clearInterval(hardRefreshTimerRef.current)
      }
    }
  }, [])

  const handleHardRefresh = async () => {
    if (isHardRefreshDisabled) return

    const now = Date.now()
    localStorage.setItem(HARD_REFRESH_STORAGE_KEY, now.toString())
    setIsHardRefreshDisabled(true)
    setHardRefreshRemainingSeconds(Math.ceil(HARD_REFRESH_COOLDOWN_MS / 1000))

    if (hardRefreshTimerRef.current) {
      clearInterval(hardRefreshTimerRef.current)
    }
    hardRefreshTimerRef.current = setInterval(() => {
      setHardRefreshRemainingSeconds((prev) => {
        if (prev === null) return null
        if (prev <= 1) {
          if (hardRefreshTimerRef.current) {
            clearInterval(hardRefreshTimerRef.current)
          }
          localStorage.removeItem(HARD_REFRESH_STORAGE_KEY)
          setIsHardRefreshDisabled(false)
          return null
        }
        return prev - 1
      })
    }, 1000)

    try {
      // Ask backend to clear auth/session (HttpOnly cookies) if present
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      })
    } catch (error) {
      console.error("[HardRefresh] Logout call failed:", error)
      // Even if logout fails, proceed to clear client-side state and reload
    }

    // Clear client-side state we control
    try {
      sessionStorage.clear()
      localStorage.removeItem("userName")
      localStorage.removeItem("userEmail")
      localStorage.removeItem("roles")
      localStorage.removeItem("id")
      localStorage.removeItem("userPhoto")
      localStorage.removeItem("roleId")
    } catch (error) {
      console.error("[HardRefresh] Error clearing storage:", error)
    }

    // Force a full reload; AuthGate/MSAL will re-establish session as needed
    window.location.href = "/"
  }

  // Set up heartbeat interval for QA/Production environments
  useEffect(() => {
    const sysEnv = (process.env.REACT_APP_SYS_ENV || "").toLowerCase()
    
    // Only run heartbeat in QA/Production where MSAL is used
    if (sysEnv === "prod" || sysEnv === "qa") {
      // Heartbeat function to check session validity
      const checkHeartbeat = async () => {
        try {
          const response = await fetch('/Routing/hb', {
            method: 'GET',
            credentials: 'include', // Include cookies for session
          })
          
          if (!response.ok) {
            throw new Error('Heartbeat request failed')
          }

          const data = await response.json()
          
          if (!data.ok) {
            // Session expired, redirect to login
            //console.log("[Heartbeat] Session expired, redirecting to login")
            // Clear any stored user data
            sessionStorage.clear()
            localStorage.removeItem("userName")
            localStorage.removeItem("userEmail")
            localStorage.removeItem("roles")
            localStorage.removeItem("id")
            // Redirect to login
            window.location.href = '/'
          }
        } catch (error) {
          // On error, log but don't redirect (network issues shouldn't log user out)
          console.error("[Heartbeat] Error checking heartbeat:", error)
        }
      }

      // Check heartbeat immediately on mount
      checkHeartbeat()
      
      // Set up interval to check every 30 seconds
      heartbeatIntervalRef.current = setInterval(() => {
        checkHeartbeat()
      }, 30000) // 30 seconds

      // Cleanup interval on unmount
      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
        }
      }
    }
  }, [])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b px-4 py-3 h-16 bg-white border-gray-200`}
    >
      <div className="flex items-center justify-between h-full">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Always-visible Hamburger */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onSidebarToggle();
            }}
            className="p-2"
          >
            <Menu className={`w-5 h-5 text-gray-600`} />
          </Button>

          {/* Company Logo & Title */}
          <div className="flex items-center gap-3">
            <img
              src="/images/uci-logo.png"
              alt="UCI Logo"
              width={32}
              height={32}
              className="rounded-full"
            />
            <h1
              className={`text-base sm:text-lg font-semibold leading-tight
                whitespace-normal break-words max-w-[150px] sm:max-w-none
                text-gray-800`}
            >
              {getSystemName()}
            </h1>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Hard Refresh button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleHardRefresh}
            disabled={isHardRefreshDisabled}
            className="flex items-center gap-1 text-xs sm:text-sm"
            title={
              isHardRefreshDisabled && hardRefreshRemainingSeconds !== null
                ? `Hard refresh available in ${Math.ceil(hardRefreshRemainingSeconds / 60)} min`
                : "Force session reset and reload"
            }
          >
            <RotateCcw className="w-4 h-4" />
            <span>Hard refresh</span>
          </Button>

          {/* Notifications */}
          <Button
            variant="ghost"
            size="sm"
            className="p-2 relative"
            onClick={() => setIsNotificationOpen(true)}
          >
            <Bell className={`w-5 h-5 text-gray-600`} />
            {notificationCount > 0 && (
              <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs bg-red-500 text-white">
                {notificationCount}
              </Badge>
            )}
          </Button>

          {/* User Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2 sm:px-3 py-2">
                <img
                  src={userPhoto || "/images/placeholder-user.jpg"}
                  alt="profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span
                  className={`hidden sm:inline text-sm font-medium
                 text-gray-700`}
                >
                  {userName}
                </span>
              </Button>
            </DropdownMenuTrigger>
          </DropdownMenu>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={isNotificationOpen}
        onClose={() => {
          setIsNotificationOpen(false)
        }}
        onNotificationCountChange={setNotificationCount}
      />
    </header>
  )
}

